/**
 * Membership Order API wrapper (DNKADM-0222).
 * DNKADM-0374 (DNKAPI-0531): adds `billingAnchor` on the quote (Defer vs Pay-through)
 * and `proratePartialPeriod: true` on card orders when the admin picks Pay-through.
 * `proratePartialPeriod` is ONLY sent when exactly `true`; omit for Defer / comp / one-time.
 *
 * Creates and reads member-scoped orders on the v2 bearer-only admin surface. This
 * slice ships comp membership orders in the UI, while the request types already
 * carry the card shape DNKADM-0223 will use.
 *
 * DNKADM-0233 adds the membership **plan change**, which answers with the SAME order
 * model and therefore lives here beside {@link parseOrder} — even though its endpoint
 * hangs off the `/v2/admin/memberships` subscription surface rather than this module's
 * `/v2/admin/members` base.
 *
 * DNKADM-0241 widens the same three endpoints (`/pricing/quote`, `/orders`,
 * `/orders/{orderId}`) for the sell cart: `package` + `credit` lines beside
 * `membership_plan`, an `external` payment mode, a `locationId` on the quote (it
 * resolves the tax jurisdiction), and a `taxDeferred`-aware quote whose `tax`/
 * `total` are `null` — NOT `0` — when the server could not resolve tax.
 *
 * DNKADM-0253 lets a card payment name a card ALREADY ON FILE (`payment.cardId`,
 * `src/api/memberCards.ts`) instead of a fresh `cardToken`. The two credentials are
 * mutually exclusive — see {@link CardOrderPaymentInput}, where the exclusivity is
 * enforced in the TYPE so a body carrying both, or neither, cannot be constructed.
 */
import { request } from './client';
import { ApiError } from './errors';
import { v2ErrorCode } from './v2Errors';
import type { OrderStatus } from '@/domain/orderStatus';

const PATH = '/v2/admin/members';

const ORDER_FIELDS = {
  compValue: ['comp_value', 'compValue'],
} as const;

const ORDER_ACTION_PATHS = {
  refund: 'refund',
  void: 'void',
} as const;


interface MembershipPlanOrderLineInput {
  type: 'membership_plan';
  refId: number;
  compTermMonths?: number;
  compEndDate?: string;
  /** Negotiated recurring pre-tax rate in integer minor units (DNKADM-0335). */
  overrideBase?: number;
}

/**
 * A one-time package purchase (`offerings.kind = 'package'`, DNKADM-0241). qty is
 * always 1 — the server rejects anything else on a package line — so it is not
 * modelled: the cart renders a package as an add/remove row, never a stepper.
 */
interface PackageOrderLineInput {
  type: 'package';
  refId: number;
  /** Negotiated pre-tax price in integer minor units (DNKADM-0334). */
  overrideBase?: number;
}

/**
 * Bucket credits (DNKADM-0241). `qty` is REQUIRED and `>= 1`, capped at the
 * bucket's `maxPurchaseQuantity`. Duplicate `refId` lines are merged server-side
 * and the cap applies to the SUMMED quantity, so a cart emits ONE line per bucket.
 */
interface CreditOrderLineInput {
  type: 'credit';
  refId: number;
  qty: number;
}

/**
 * The line types this module can order. `membership_plan` cannot be mixed with the
 * other two in one cart (`400 UNSUPPORTED_IN_SLICE`) — plans keep their own dialog.
 *
 * Exported (DNKAPI-0497 / DNKADM-0347): the walk-in counter sale (`@/api/walkInSale`)
 * now delegates to this SAME order surface and reuses this exact line shape rather
 * than declaring a parallel type.
 */
export type OrderLineInput =
  | MembershipPlanOrderLineInput
  | PackageOrderLineInput
  | CreditOrderLineInput;

interface CompOrderPaymentInput {
  mode: 'comp';
  locationId: number;
  idempotencyKey: string;
  reason?: string;
}

/** What every card payment carries, whichever credential identifies the card. */
interface CardOrderPaymentBase {
  mode: 'card';
  locationId: number;
  idempotencyKey: string;
}

/** A freshly tokenized card — the single-use nonce from the browser. */
interface CardTokenPaymentInput extends CardOrderPaymentBase {
  cardToken: string;
  cardId?: never;
}

/** A card already on file (DNKADM-0253) — the opaque saved-card id. */
interface CardOnFilePaymentInput extends CardOrderPaymentBase {
  cardId: string;
  cardToken?: never;
}

/**
 * A card payment carries **exactly one** credential (DNKADM-0253): a fresh
 * `cardToken` **or** a saved `cardId`. Sending both, or neither, is a `400` server-side
 * and nothing is charged — so the `?: never` arms make that a COMPILE error instead of
 * a runtime rejection. The unused key is simply never set, so the serialized body omits
 * it entirely rather than carrying an `undefined`.
 */
type CardOrderPaymentInput = CardTokenPaymentInput | CardOnFilePaymentInput;

/**
 * Payment taken OUTSIDE the PSP (DNKADM-0241) — external records **how** it was
 * paid, never how much: no amount is ever sent.
 */
interface ExternalOrderPaymentInput {
  mode: 'external';
  locationId: number;
  idempotencyKey: string;
  status: 'paid' | 'pending';
  /** Required when `status` is `'paid'` — a `v2_tenant_payment_method.id`. */
  paymentMethodId?: string;
}

/**
 * Exported (DNKAPI-0497 / DNKADM-0347): the walk-in counter sale (`@/api/walkInSale`)
 * reuses this exact payment shape rather than declaring a parallel type.
 */
export type OrderPaymentInput =
  | CompOrderPaymentInput
  | CardOrderPaymentInput
  | ExternalOrderPaymentInput;

/**
 * Exported (DNKAPI-0497 / DNKADM-0347): reused verbatim by the walk-in counter sale.
 */
export interface OverrideAuthInput {
  pin: string;
}

export interface CreateMemberOrderInput {
  lines: OrderLineInput[];
  payment: OrderPaymentInput;
  /**
   * One transaction-level PIN whenever any line carries `overrideBase`; on a comp order,
   * the manager PIN the server checks (DNKADM-0400).
   */
  overrideAuth?: OverrideAuthInput;
  /**
   * DNKADM-0374: send `true` ONLY for a Pay-through (prorated) anchored card order.
   * Omit for Defer, comp, one-time, and any non-anchored order. Never send `false`.
   */
  proratePartialPeriod?: boolean;
}

/**
 * One priced row of a quote (DNKADM-0241) — the cart reads its per-row figures
 * from here, keyed on `refId`, and NEVER recomputes a line's tax.
 */
export interface MembershipQuoteLine {
  type: string | null;
  refId: number | null;
  qty: number | null;
  base: number;
  /** `null` when the server deferred tax for this line — never render it as 0. */
  tax: number | null;
  total: number | null;
  currency: string | null;
}

/**
 * Prorated stub for a Pay-through anchored card order (DNKADM-0374, DNKAPI-0531).
 * `tax`/`total` follow the same null-trichotomy as plan `tax`/`total` — `null`
 * means "tax at checkout", NEVER coerce to `0`. Do not render `$0.00` for null.
 */
export interface BillingAnchorProrate {
  days: number;
  base: number;
  /** `null` = tax calculated at checkout; never coerce to 0. */
  tax: number | null;
  /** `null` when `tax` is null. */
  total: number | null;
  amountMinor: number | null;
  currency: string | null;
  credits: Array<{
    bucketId: number;
    bucketName: string | null;
    /** Integer (server-rounded). */
    amount: number;
    unit: string | null;
  }>;
}

/**
 * The anchored billing result on a membership quote (DNKADM-0374, DNKAPI-0531).
 * Present when the quote was posted with a `locationId` and the resolved location
 * has a billing day (tenant or location override). `available: false` means the
 * location's card processor cannot anchor (e.g. TiloPay) — hide Pay-through, do
 * not 422. `prorate` is absent when `available` is `false` or the prorated amount
 * is below the server floor.
 */
export interface BillingAnchor {
  /** The resolved day of month (1–28). */
  day: number;
  /** First full-plan charge date in `YYYY-MM-DD` local calendar. */
  startOn: string;
  /** `false` = processor cannot anchor (TiloPay etc.) — hide Pay-through. */
  available: boolean;
  /** Only present when `available === true` AND a prorated amount was computed. */
  prorate?: BillingAnchorProrate;
}

export interface MembershipQuote {
  subtotal: number;
  /**
   * The real tax, or `null` when {@link MembershipQuote.taxDeferred} is `true`.
   * `null` is NOT zero: it means the jurisdiction could not be resolved, so no
   * figure may be rendered for it (show "Tax calculated at checkout" instead).
   */
  tax: number | null;
  /** The amount that will actually be charged, or `null` when tax is deferred. */
  total: number | null;
  currency: string | null;
  /**
   * The server deferred tax (a taxable item with an unresolvable jurisdiction) ⇒
   * `tax`/`total` are `null`. The ONLY case that shows "Tax calculated at
   * checkout"; a pay button must never then carry the pre-tax figure.
   */
  taxDeferred: boolean;
  /** The priced rows, `[]` when the server sent none. */
  lines: MembershipQuoteLine[];
  /**
   * Present when the quote was posted with a `locationId` and a billing day was
   * resolved (DNKADM-0374, DNKAPI-0531). Absent on unanchored quotes (today's
   * dialog unchanged). Parse defensively — absent/malformed → key omitted.
   */
  billingAnchor?: BillingAnchor;
}

interface MembershipOrderLine {
  type: string | null;
  refId: number | null;
  tier: string | null;
  base: number;
  tax: number;
  total: number;
  subscriptionId: string | null;
  expiration: string | null;
  /** Line status (DNKADM-0442); open-ended, kept verbatim. Null when the API sent none. */
  status: OrderStatus | null;
}

interface MembershipOrderPayment {
  id: string | null;
  mode: string | null;
  status: string | null;
  amount: number | null;
  currency: string | null;
}

type MembershipOrderPaymentAction = Record<string, unknown> | null;

export interface MembershipOrder {
  orderId: number | null;
  orderNumber: string | null;
  status: OrderStatus;
  currency: string | null;
  subtotal: number;
  taxTotal: number;
  grandTotal: number;
  compValue: number | null;
  lines: MembershipOrderLine[];
  payments: MembershipOrderPayment[];
  paymentAction: MembershipOrderPaymentAction;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function asNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function asIdText(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asText(value);
}

function parseLine(raw: unknown): MembershipOrderLine | null {
  if (!isObject(raw)) return null;
  return {
    type: asText(raw['type']),
    refId: asNumber(raw['refId']),
    tier: asText(raw['tier']),
    base: asNumber(raw['base']) ?? 0,
    tax: asNumber(raw['tax']) ?? 0,
    total: asNumber(raw['total']) ?? 0,
    subscriptionId: asIdText(raw['subscriptionId']),
    expiration: asText(raw['expiration']),
    status: asText(raw['status']),
  };
}

function parsePayment(raw: unknown): MembershipOrderPayment | null {
  if (!isObject(raw)) return null;
  return {
    id: asIdText(raw['id'] ?? raw['paymentId']),
    mode: asText(raw['mode']),
    status: asText(raw['status']),
    amount: asNumber(raw['amount'] ?? raw['total']),
    currency: asText(raw['currency']),
  };
}

function parsePaymentAction(raw: unknown): MembershipOrderPaymentAction {
  return isObject(raw) ? { ...raw } : null;
}

function parseCompValue(raw: Record<string, unknown>): number | null {
  const value = asNumber(raw[ORDER_FIELDS.compValue[0]] ?? raw[ORDER_FIELDS.compValue[1]]);
  return value !== null && value > 0 ? value : null;
}

/**
 * Exported (DNKAPI-0497 / DNKADM-0347): the walk-in counter sale reuses this parser
 * verbatim and extends its result with its own `contactId`/`invited` fields, rather
 * than writing a second parser for the same receipt shape.
 */
export function parseOrder(raw: unknown): MembershipOrder {
  const o = isObject(raw) ? raw : {};
  const rawLines = Array.isArray(o['lines']) ? o['lines'] : [];
  const rawPayments = Array.isArray(o['payments']) ? o['payments'] : [];
  return {
    orderId: asNumber(o['orderId'] ?? o['id']),
    orderNumber: asText(o['orderNumber']),
    status: (asText(o['status']) ?? 'FAILED') as OrderStatus,
    currency: asText(o['currency']),
    subtotal: asNumber(o['subtotal']) ?? 0,
    taxTotal: asNumber(o['taxTotal']) ?? 0,
    grandTotal: asNumber(o['grandTotal']) ?? 0,
    compValue: parseCompValue(o),
    lines: rawLines.map(parseLine).filter((line): line is MembershipOrderLine => line !== null),
    payments: rawPayments
      .map(parsePayment)
      .filter((payment): payment is MembershipOrderPayment => payment !== null),
    paymentAction: parsePaymentAction(o['paymentAction']),
  };
}

function orderPath(contactId: string): string {
  return `${PATH}/${encodeURIComponent(contactId)}/orders`;
}

function orderItemPath(contactId: string, orderId: number | string): string {
  return `${orderPath(contactId)}/${encodeURIComponent(String(orderId))}`;
}

function orderActionPath(
  contactId: string,
  orderId: number | string,
  action: keyof typeof ORDER_ACTION_PATHS,
): string {
  return `${orderItemPath(contactId, orderId)}/${ORDER_ACTION_PATHS[action]}`;
}

function quotePath(contactId: string): string {
  return `${PATH}/${encodeURIComponent(contactId)}/pricing/quote`;
}

function parseQuoteLine(raw: unknown): MembershipQuoteLine | null {
  if (!isObject(raw)) return null;
  return {
    type: asText(raw['type']),
    refId: asNumber(raw['refId']),
    qty: asNumber(raw['qty']),
    base: asNumber(raw['base']) ?? 0,
    // A deferred line carries no tax/total — kept null, never coerced to 0.
    tax: asNumber(raw['tax']),
    total: asNumber(raw['total']),
    currency: asText(raw['currency']),
  };
}

function parseBillingAnchorCredit(raw: unknown): BillingAnchorProrate['credits'][number] | null {
  if (!isObject(raw)) return null;
  const bucketId = asNumber(raw['bucketId']);
  if (bucketId === null || !Number.isInteger(bucketId) || bucketId < 1) return null;
  return {
    bucketId: Math.trunc(bucketId),
    bucketName: asText(raw['bucketName']),
    amount: Math.trunc(asNumber(raw['amount']) ?? 0),
    unit: asText(raw['unit']),
  };
}

/**
 * Parse the prorate stub (DNKADM-0374). Returns `undefined` when missing or
 * unparseable. `tax`/`total` stay `null` when the server sends `null` — the
 * trichotomy from DNKADM-0241 applies verbatim; never coerce to `0`.
 */
function parseBillingAnchorProrate(raw: unknown): BillingAnchorProrate | undefined {
  if (!isObject(raw)) return undefined;
  const base = asNumber(raw['base']);
  if (base === null) return undefined;
  // tax/total: JSON null → null (tax at checkout); absent → null (treat as null).
  const taxRaw = raw['tax'];
  const totalRaw = raw['total'];
  const tax = taxRaw === null || taxRaw === undefined ? null : asNumber(taxRaw);
  const total = totalRaw === null || totalRaw === undefined ? null : asNumber(totalRaw);
  const creditsRaw = Array.isArray(raw['credits']) ? raw['credits'] : [];
  const credits = creditsRaw
    .map(parseBillingAnchorCredit)
    .filter((c): c is NonNullable<typeof c> => c !== null);
  return {
    days: asNumber(raw['days']) ?? 0,
    base,
    tax,
    total,
    amountMinor: asNumber(raw['amountMinor']),
    currency: asText(raw['currency']),
    credits,
  };
}

/**
 * Parse the billing anchor block (DNKADM-0374, DNKAPI-0531). Returns `undefined`
 * when the object is missing/malformed (day out-of-range, no startOn, no boolean
 * `available`). `prorate` is only parsed when `available === true`.
 */
function parseBillingAnchor(raw: unknown): BillingAnchor | undefined {
  if (!isObject(raw)) return undefined;
  const dayRaw = asNumber(raw['day']);
  if (dayRaw === null || !Number.isInteger(dayRaw)) return undefined;
  const day = Math.trunc(dayRaw);
  if (day < 1 || day > 28) return undefined;
  const startOn = asText(raw['startOn']);
  if (!startOn) return undefined;
  if (typeof raw['available'] !== 'boolean') return undefined;
  const available = raw['available'];
  const result: BillingAnchor = { day, startOn, available };
  if (available) {
    const prorate = parseBillingAnchorProrate(raw['prorate']);
    if (prorate !== undefined) result.prorate = prorate;
  }
  return result;
}

/**
 * Parse a quote (DNKADM-0241). `taxDeferred` is read from the `cart` object and
 * defaults to `false`; when it is true, `tax`/`total` are `null` rather than the
 * legacy `0` coercion, so the UI can tell a deferred tax from a real zero. When it
 * is false the historic `asNumber(...) ?? 0` behavior is preserved EXACTLY.
 * `lines[]` is read from the envelope root first (where the server sends it beside
 * `cart`), then from the cart itself for the legacy flat shape.
 */
function parseQuote(raw: unknown): MembershipQuote {
  const root = isObject(raw) ? raw : {};
  const q = isObject(root['cart']) ? root['cart'] : root;
  const taxDeferred = q['taxDeferred'] === true;
  const rawLines = Array.isArray(root['lines'])
    ? root['lines']
    : Array.isArray(q['lines'])
      ? q['lines']
      : [];
  // DNKADM-0374: billing anchor — check root first, then cart. Absent/malformed → omit.
  const rawAnchor = root['billingAnchor'] ?? q['billingAnchor'];
  const billingAnchor = parseBillingAnchor(rawAnchor);
  const quote: MembershipQuote = {
    subtotal: asNumber(q['subtotal']) ?? 0,
    tax: taxDeferred ? null : (asNumber(q['tax'] ?? q['taxTotal']) ?? 0),
    total: taxDeferred ? null : (asNumber(q['total'] ?? q['grandTotal']) ?? 0),
    currency: asText(q['currency']),
    taxDeferred,
    lines: rawLines
      .map(parseQuoteLine)
      .filter((line): line is MembershipQuoteLine => line !== null),
  };
  if (billingAnchor !== undefined) quote.billingAnchor = billingAnchor;
  return quote;
}

/**
 * `POST {base}/{contactId}/pricing/quote` → the priced cart. `locationId` is the
 * member's service location (it resolves the tax jurisdiction); when it is
 * omitted the key is left off the body entirely, so a caller that does not pass
 * one sends the exact body it always did. `overrideAuth` is likewise omitted unless
 * a caller supplies the one transaction-level PIN for negotiated package lines.
 */
export async function getMembershipQuote(
  contactId: string,
  lines: OrderLineInput[],
  locationId?: number,
  overrideAuth?: OverrideAuthInput,
): Promise<MembershipQuote> {
  const json: { lines: OrderLineInput[]; locationId?: number; overrideAuth?: OverrideAuthInput } = {
    lines,
  };
  if (locationId !== undefined) json.locationId = locationId;
  if (overrideAuth !== undefined) json.overrideAuth = overrideAuth;
  const body = await request<unknown>('POST', quotePath(contactId), { json });
  return parseQuote(body);
}

export async function createMemberOrder(
  contactId: string,
  input: CreateMemberOrderInput,
): Promise<MembershipOrder> {
  // DNKADM-0374: include proratePartialPeriod ONLY when exactly true (Pay-through
  // anchored card order). Omit for Defer, comp, one-time, and any non-anchored order.
  const json: Record<string, unknown> = {
    lines: input.lines,
    payment: input.payment,
  };
  if (input.overrideAuth !== undefined) json['overrideAuth'] = input.overrideAuth;
  if (input.proratePartialPeriod === true) json['proratePartialPeriod'] = true;
  const body = await request<unknown>('POST', orderPath(contactId), { json });
  return parseOrder(body);
}

export async function getMemberOrder(
  contactId: string,
  orderId: number | string,
): Promise<MembershipOrder> {
  const body = await request<unknown>('GET', orderItemPath(contactId, orderId));
  return parseOrder(body);
}

export async function refundMemberOrder(
  contactId: string,
  orderId: number | string,
): Promise<MembershipOrder> {
  const body = await request<unknown>('POST', orderActionPath(contactId, orderId, 'refund'));
  return parseOrder(body);
}

export async function voidMemberOrder(
  contactId: string,
  orderId: number | string,
): Promise<MembershipOrder> {
  const body = await request<unknown>('POST', orderActionPath(contactId, orderId, 'void'));
  return parseOrder(body);
}

// --- Membership plan change (DNKADM-0233) ---------------------------------------
//
// FORWARD CONTRACT — SHAPE. `change-plan` is pay-api-gated and was NOT on staging when
// this landed, so it is built to the spec's shape and unit-tested against MSW only.
//
// **The FE never decides upgrade vs downgrade.** It posts `toPlanId` and RENDERS the
// response: dink derives the direction from the plan prices and answers either with an
// immediate charge (an ordinary order/receipt) or a scheduled record carrying the
// effective/renewal date. Nothing below compares prices.
//
// Every wire string the contract could rename is isolated in the three constants that
// follow, so a divergence is a one-line edit and nothing else moves.

/**
 * Path pieces of the change-plan endpoint:
 * `POST {base}/{contactId}/{subscription}/{action}`. Spelled here rather than shared
 * with `membershipSubscription.ts` so a rename of this forward-contract endpoint cannot
 * disturb the shipped pause/resume/cancel paths.
 */
const CHANGE_PLAN_PATH = {
  base: '/v2/admin/memberships',
  subscription: 'subscription',
  action: 'change-plan',
} as const;

/**
 * Wire field names of the change-plan request/response. Each list is tried in order and
 * the FIRST present value wins, so an alias rename needs only its entry edited.
 *
 * - `toPlanId` — the single request body key (the target plan).
 * - `order` — optional wrapper key when the receipt is nested rather than at the root.
 * - `effectiveDate` — **where the renewal/effective date lives** for a scheduled change.
 * - `changeType` — the server's **immediate-vs-scheduled indicator** (see
 *   {@link CHANGE_PLAN_OUTCOMES} for its tokens).
 * - `scheduled` — a boolean form of the same indicator, if that is how it ships.
 */
const CHANGE_PLAN_FIELDS = {
  toPlanId: 'toPlanId',
  order: 'order',
  effectiveDate: ['effectiveDate', 'scheduledFor', 'effectiveAt', 'renewalDate'],
  changeType: ['changeType', 'direction', 'changeDirection', 'effect'],
  scheduled: ['scheduled', 'isScheduled', 'deferred'],
} as const;

/**
 * The tokens {@link CHANGE_PLAN_FIELDS.changeType} can carry. `immediate` ⇒ the plan
 * swapped now and the receipt is a real charge; `scheduled` ⇒ nothing changed yet and the
 * change lands on the effective date.
 *
 * Matched case-insensitively with `_`/`-` normalized, on **whole segments** — never a raw
 * substring (see {@link matchesOutcome}). Raw `includes` would classify the wire token
 * `UNKNOWN` as immediate, because `'unknown'` literally contains `'now'`; a server that
 * explicitly declines to state a direction must NOT have one invented for it.
 */
const CHANGE_PLAN_OUTCOMES = {
  immediate: ['immediate', 'upgrade'],
  scheduled: ['scheduled', 'downgrade', 'at_renewal', 'at_period_end', 'deferred'],
} as const;

/**
 * What the server said happened. `unknown` is a local guard value, never a wire token:
 * it means the response signalled NEITHER an indicator, NOR an effective date, NOR a
 * charge — the UI then states only what the receipt shows and claims no direction.
 */
type MembershipPlanChangeEffect = 'immediate' | 'scheduled' | 'unknown';

/** The parsed change-plan result: the order/receipt plus the scheduled-effective marker. */
export interface MembershipPlanChange {
  /** The plan-change order — the same model Buy returns. */
  order: MembershipOrder;
  /** Server-signalled outcome; the ONLY thing the UI branches its copy on. */
  effect: MembershipPlanChangeEffect;
  /** When a scheduled change takes effect (server text, rendered verbatim), or null. */
  effectiveDate: string | null;
}

/** First non-null value among `keys`, read through `read`. */
function firstOf<T>(
  raw: Record<string, unknown>,
  keys: readonly string[],
  read: (value: unknown) => T | null,
): T | null {
  for (const key of keys) {
    const value = read(raw[key]);
    if (value !== null) return value;
  }
  return null;
}

/** A real boolean from any of `keys`, or null when none is present. */
function firstBoolean(raw: Record<string, unknown>, keys: readonly string[]): boolean | null {
  for (const key of keys) {
    if (typeof raw[key] === 'boolean') return raw[key];
  }
  return null;
}

/** Normalize an indicator token for comparison: trimmed, lower-cased, `-` → `_`. */
function outcomeToken(value: string): string {
  return value.trim().toLowerCase().replace(/-/g, '_');
}

/**
 * Does a normalized wire `token` carry one of `entries` as a WHOLE segment? `_`-padding
 * both sides turns `includes` into a word-boundary match, so `immediate_upgrade` matches
 * `upgrade` while `unknown` matches nothing. Substring matching would not be safe here.
 */
function matchesOutcome(token: string, entries: readonly string[]): boolean {
  const padded = `_${token}_`;
  return entries.some((entry) => padded.includes(`_${entry}_`));
}

/**
 * Resolve the outcome from the RESPONSE ALONE, strongest signal first:
 *  1. the explicit `changeType`/`direction` indicator,
 *  2. the boolean `scheduled` flag,
 *  3. **a positive charge (`grandTotal > 0`)** — money moved, so the change is in effect
 *     NOW. This outranks the date because the spec makes it decisive in one direction: a
 *     deferred change is defined as "no charge, no refund", so a charge cannot be deferred.
 *  4. an effective/renewal date, the last-resort deferral hint.
 *
 * Rungs 3 and 4 are in that order deliberately (QA round 1). A renewal date is WEAK
 * evidence — an immediate upgrade may well carry one as informational metadata ("your next
 * renewal is …") — so letting it outrank a real charge would render a $40 upgrade as a
 * scheduled downgrade with the charge row hidden. The charge is the strong signal.
 *
 * No price comparison, no FE guess — a `$0` response with no marker stays `unknown`.
 */
function resolveEffect(
  raw: Record<string, unknown>,
  order: MembershipOrder,
  effectiveDate: string | null,
): MembershipPlanChangeEffect {
  const indicator = firstOf(raw, CHANGE_PLAN_FIELDS.changeType, asText);
  if (indicator !== null) {
    const token = outcomeToken(indicator);
    if (matchesOutcome(token, CHANGE_PLAN_OUTCOMES.immediate)) return 'immediate';
    if (matchesOutcome(token, CHANGE_PLAN_OUTCOMES.scheduled)) return 'scheduled';
  }
  const scheduledFlag = firstBoolean(raw, CHANGE_PLAN_FIELDS.scheduled);
  if (scheduledFlag !== null) return scheduledFlag ? 'scheduled' : 'immediate';
  if (order.grandTotal > 0) return 'immediate';
  if (effectiveDate !== null) return 'scheduled';
  return 'unknown';
}

function parsePlanChange(body: unknown): MembershipPlanChange {
  const root = isObject(body) ? body : {};
  const wrapped = root[CHANGE_PLAN_FIELDS.order];
  const nested = isObject(wrapped) ? wrapped : null;
  const order = parseOrder(nested ?? root);
  // The markers may ride on the envelope or on the order itself; the envelope wins, and
  // a nested receipt is consulted only when the envelope says nothing.
  const effectiveDate =
    firstOf(root, CHANGE_PLAN_FIELDS.effectiveDate, asText) ??
    (nested === null ? null : firstOf(nested, CHANGE_PLAN_FIELDS.effectiveDate, asText));
  const rootEffect = resolveEffect(root, order, effectiveDate);
  const effect =
    rootEffect !== 'unknown' || nested === null
      ? rootEffect
      : resolveEffect(nested, order, effectiveDate);
  return { order, effect, effectiveDate };
}

function changePlanPath(contactId: string): string {
  return `${CHANGE_PLAN_PATH.base}/${encodeURIComponent(contactId)}/${CHANGE_PLAN_PATH.subscription}/${CHANGE_PLAN_PATH.action}`;
}

/**
 * v2 entity ids are NUMBERS on the wire and normalized to `string` in the UI
 * (`membershipPlans.asId`); send a numeric-looking id back as a number, and pass a
 * genuinely-string id through unchanged.
 */
function toPlanIdValue(toPlanId: string | number): string | number {
  if (typeof toPlanId === 'number') return toPlanId;
  const trimmed = toPlanId.trim();
  const n = Number(trimmed);
  return trimmed.length > 0 && Number.isFinite(n) ? n : toPlanId;
}

/**
 * `POST /v2/admin/memberships/{contactId}/subscription/change-plan` ← `{ toPlanId }` →
 * the plan-change receipt. Bearer-only `/v2/admin/**` (no CSRF, no cookies). Throws a
 * typed `ApiError` on a non-2xx — a declined card on an immediate upgrade is a `402` and
 * is NEVER a success.
 */
export async function changeMembershipPlan(
  contactId: string,
  toPlanId: string | number,
): Promise<MembershipPlanChange> {
  const body = await request<unknown>('POST', changePlanPath(contactId), {
    json: { [CHANGE_PLAN_FIELDS.toPlanId]: toPlanIdValue(toPlanId) },
  });
  return parsePlanChange(body);
}

// --- An earlier card order still open (DNKAPI-0690 / DNKADM-0475) -------------------

/**
 * The open order a `409 ORDER_NEEDS_ATTENTION` names. dink-api refuses a new CARD order
 * (`POST /members/{contactId}/orders` and the walk-in sale) while the same member has a
 * card order at the same location from the last 24 hours that is `PENDING` (parked, or an
 * unknown outcome) or `NEEDS_REVIEW` (charged, nothing applied). Nothing is written and the
 * card is not touched. A same-key replay is never refused; it returns its own order.
 * The open order is not always a desk sale: it can be the member's own online purchase parked
 * on 3-D Secure, or a no-show fee charge with an unknown outcome, so the copy stays neutral.
 *
 * Every field may be missing on the wire and is read defensively.
 */
export interface OpenOrderNeedsAttention {
  orderId: number | null;
  orderNumber: string | null;
  /** `PENDING` or `NEEDS_REVIEW`, kept verbatim. */
  status: OrderStatus | null;
  /** The member the open order belongs to (on a walk-in, the contact the server matched or made). */
  contactId: string | null;
}

/** The open order a `409 ORDER_NEEDS_ATTENTION` names, or null for any other error. */
export function parseOrderNeedsAttention(error: unknown): OpenOrderNeedsAttention | null {
  if (!(error instanceof ApiError) || v2ErrorCode(error) !== 'ORDER_NEEDS_ATTENTION') return null;
  const d = error.details ?? {};
  return {
    orderId: asNumber(d['openOrderId']),
    orderNumber: asText(d['openOrderNumber']),
    status: asText(d['openOrderStatus']),
    contactId: asText(d['contactId']),
  };
}
